<template>
  <div>
    <v-progress-circular
        v-if="isLoading"
        :size="70"
        :width="7"
        color="primary"
        indeterminate
        class="centered"
    />
    <div v-else>
      <h1 class="text-center mt-3">Public Holidays</h1>

      <!-- Filters -->
      <div class="form shadow p-5 mb-5 bg-white rounded">
        <!-- Action Row -->
        <div class="d-flex px-5 mb-2">
          <button class="btn btn-outline-danger" @click="uploadFiles">
            Import Public holidays
          </button>
        </div>

        <!-- Download link row (left, new line) -->
        <div class="px-5 mb-3">
          <a
              href="#"
              class="d-block text-start"
              target="_blank"
              @click.prevent="downloadSampleFile"
          >
            Download sample file
          </a>
        </div>

        <!-- Import Result -->
        <div
            v-if="importResult.visible"
            class="result-card shadow p-4 mt-4 mb-4 bg-white rounded position-relative"
            style="max-width: 720px"
        >
          <button
              type="button"
              class="close-btn"
              aria-label="Close"
              @click="importResult.visible = false"
          >
            ×
          </button>

          <h3 class="mb-4"><strong>สรุปผลการนำเข้าข้อมูล</strong></h3>
          <div class="d-flex justify-content-between mb-2">
            <span>ทำรายการสำเร็จ</span>
            <span>{{ importResult.successCount }} รายการ</span>
          </div>
          <div class="d-flex justify-content-between mb-4 text-danger">
            <span>ทำรายการไม่สำเร็จ ข้อมูลไม่ถูกต้อง</span>
            <span>{{ importResult.failedCount }} รายการ</span>
          </div>

          <div class="mb-2"><strong>ปัญหาที่พบ</strong></div>
          <div class="mb-3">{{ importResult.message || '-' }}</div>
          <div class="mb-2 d-flex font-weight-bold">
            <div class="error-col-field text-center">บรรทัดที่</div>
            <div class="error-col-msg">ปัญหาที่พบ</div>
          </div>

          <div v-if="(importResult.errors || []).length === 0" class="text-muted">
            — ไม่มีข้อผิดพลาด —
          </div>
          <ul v-else class="list-unstyled mb-0">
            <li v-for="(err, idx) in importResult.errors" :key="idx" class="d-flex mb-1">
              <div class="error-col-field text-center">{{ err.fieldName }}</div>
              <div class="error-col-msg">{{ err.message }}</div>
            </li>
          </ul>
        </div>

        <!-- Year / Month / Buttons -->
        <div class="row mt-3">
          <div class="col-12 col-md-4 mb-3">
            <label for="year">ปี</label>
            <multiselect
                v-model="yearSelected"
                :options="years"
                placeholder="Select Year"
            />
          </div>
          <div class="col-12 col-md-4 mb-3">
            <label for="month">เดือน</label>
            <multiselect
                v-model="monthSelected"
                :options="months"
                label="text"
                track-by="id"
                placeholder="Select Month"
            />
          </div>
          <div class="col-12 col-md-4 mb-3 d-flex align-items-end gap-2">
            <button class="btn btn-outline-secondary flex-fill" @click="resetFilter">
              Reset
            </button>
            <button class="btn btn-outline-primary flex-fill" @click="getHolidayList(sortState)">
              ค้นหา
            </button>
            <button class="btn btn-outline-success flex-fill" @click="openModal">
              เพิ่ม
            </button>
          </div>
        </div>

        <!-- Results (Table) -->
        <div class="mt-3">
          <div class="row mb-2">
            <div class="d-flex align-items-center justify-content-start col">
              <span>ผลการค้นหา: {{ totalItems }}</span>
            </div>
          </div>

          <b-table
              class="table table-bordered"
              hover
              responsive
              :fields="holidayFields"
              :items="holidayItems"
              :per-page="perPage"
              :busy="isBusy"
              @sort-changed="sortCompare"
          >
            <template #cell(action)="data">
              <div class="d-flex">
                <button
                    class="btn btn-outline-danger btn-sm"
                    @click="deleteHolidayById(data.item.id)"
                >
                  Delete
                </button>
              </div>
            </template>
          </b-table>

          <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              class="my-0"
              @input="changePage"
          />
        </div>
      </div>
    </div>

    <!-- Modal: Add Holiday -->
    <div v-if="showModal" class="modal-backdrop">
      <div class="modal-content shadow rounded p-5 bg-white position-relative">
        <button type="button" class="close-btn" aria-label="Close" @click="closeModal">×</button>
        <h3 class="mb-4"><strong>เพิ่มวันหยุด</strong></h3>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="newHolidayDate">วันที่</label>
            <date-picker
                id="newHolidayDate"
                v-bind="$attrs"
                v-model="newHoliday.date"
                format="D MMMM yyyy"
                lang="en"
                :value="newHoliday.date"
                @change="newHoliday.date = $event"
                style="margin-top: -0.6rem"
            />
          </div>
          <div class="col-md-6 mb-3">
            <label for="detail">รายละเอียด</label>
            <input type="text" class="form-control" v-model="newHoliday.name" />
          </div>
        </div>
        <div class="text-right">
          <button class="btn btn-outline-success" @click="saveHoliday">Save</button>
        </div>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
        id="uploadFileExcel"
        ref="uploadFileExcel"
        type="file"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        hidden
        @change="onSelectFile"
    />
    <CustomConfirmDialog
      v-model="isShowConfirmDialog"
      :message="selectedFile
        ? 'คุณต้องการ import<br>' + selectedFile.name + '?'
        : 'คุณต้องการ import ไฟล์นี้หรือไม่?'"
      confirmButtonLabel="ใช่"
      cancelButtonLabel="ปิด"
      @confirm="uploadExcel"
    />
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import moment from "moment";
import PublicHolidaysService from "services/api/publicHolidays.service";
import PresignedUploadUrlService from "services/api/presignedUploadUrl.service";
import DatePicker from "components/Datepicker/Datepicker.vue";
import CustomConfirmDialog from "components/Dialog/CustomConfirmDialog.vue";

export default {
  name: "PublicHolidayPage",
  components: { CustomConfirmDialog, DatePicker, Multiselect },
  data() {
    return {
      isLoading: false,
      isBusy: false,
      isShowConfirmDialog: false,
      years: [],
      months: [],
      yearSelected: moment().format("YYYY"),
      monthSelected: null,
      searchText: "",
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      sortState: {key: "date", type: "desc"},
      holidayFields: [
        {key: "date", label: "วันที่", sortable: true},
        {key: "name", label: "รายละเอียด", sortable: true},
        {key: "action", label: "Action"},
      ],
      holidayItems: [],
      importResult: {
        visible: false,
        successCount: 0,
        failedCount: 0,
        message: "",
        errors: [],
      },
      error: {
        errorDate: null,
      },
      showModal: false,
      newHoliday: {date: "", name: ""},
      resultPath : null,
      selectedFile: null,
    };
  },
  mounted() {
    this.setYears();
    this.setMonths();
    this.getHolidayList(this.sortState);
  },
  methods: {
    uploadFiles() {
      this.$refs.uploadFileExcel?.click();
    },

    onSelectFile(event) {
      const file = event.target.files[0];

      if (!file) {
        alert("กรุณาเลือกไฟล์");
        return;
      }

      this.selectedFile = file;
      this.isShowConfirmDialog = true;

      event.target.value = "";
    },

    async uploadExcel() {
      try {
        this.isLoading = true;
        this.importResult.visible = false;

        const file = this.selectedFile;
        await PresignedUploadUrlService.getS3SignUrl("holidays", file)
            .then(async (res) => {
              const data = res.data;
              this.resultPath = data.key;
              await PresignedUploadUrlService.pushProfilePhotoToS3(data.presignedUrl, file);
            })
            .catch((err) => {
              console.log("Error: ", err.response);
            });

        const importRes = await PublicHolidaysService.importFromS3Key(this.resultPath);
        const payload = importRes?.data || {errors: [], successCount: 0, failedCount: 0, message: ""};

        const errors = Array.isArray(payload?.errors)
            ? payload.errors.map((e) => ({
              fieldName: e?.rowIndex ?? e?.fieldName ?? "-",
              message: e?.message ?? "-",
            }))
            : [];

        this.importResult = {
          visible: true,
          successCount: Number(payload.successCount ?? 0),
          failedCount: Number(payload.failedCount ?? errors.length ?? 0),
          message: payload.message ?? "",
          errors,
        };

        await this.getHolidayList(this.sortState);
      } catch (err) {
        console.log("Error uploadExcel:", err);
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            "ข้อมูลของไฟล์ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง";
        alert(msg);
      } finally {
        this.isLoading = false;
        if (this.$refs.uploadFileExcel) this.$refs.uploadFileExcel.value = null;
      }
    },

    downloadSampleFile() {
      const base = (process.env.VUE_APP_HOST_API || "").replace(/\/?$/, "/");
      window.open(`${base}static/sample/samplePublicHolidays.xlsx`, "_blank");
    },

    setYears() {
      const currentYear = Number.parseInt(moment().format("YYYY"), 10);
      for (let y = currentYear; y >= 2000; y--) this.years.push(y.toString());
    },
    setMonths() {
      this.months = moment.months().map((name, idx) => ({id: idx + 1, text: name}));
    },
    resetFilter() {
      this.yearSelected = moment().format("YYYY");
      this.monthSelected = null;
      this.searchText = "";
      this.currentPage = 1;
      this.getHolidayList(this.sortState);
    },

    openModal() {
      this.newHoliday = {date: "", name: ""};
      this.showModal = true;
    },
    closeModal() {
      console.log("closeModal called");
      this.showModal = false; // Set showModal to false
      console.log("showModal is now:", this.showModal);
    },
    async saveHoliday() {
      if (!this.newHoliday.date || !this.newHoliday.name) return;
      try {
        this.isLoading = true;
        const response = await PublicHolidaysService.createPublicHolidays({
          date: this.newHoliday.date,
          name: this.newHoliday.name,
        });

        if (response) {
          alert('บันทึกสำเร็จ');
          this.closeModal();
        }
        await this.getHolidayList(this.sortState);
      } catch (err) {
        console.log("Error createPublicHolidays:", err);
        const errorData = err.response?.data;
        if (errorData?.errors?.length > 0) {
          this.setErrorText(errorData.errors);
        } else if (errorData?.message) {
          alert(errorData.message);
        }
      }
      finally {
        this.isLoading = false;
      }
    },


    async getHolidayList(sortParam) {
      this.isBusy = true;
      try {
        const s = sortParam || this.sortState;
        const param = {
          currentPage: this.currentPage,
          limit: this.perPage,
          sortColumn: s?.key || "date",
          sortType: s?.type || "desc",
          key: (this.searchText || "").trim(),
          year: this.yearSelected,
          month: this.monthSelected?.id ?? null,
        };

        const res = await PublicHolidaysService.getListPublicHolidays(param);
        const data =
            res?.data || {publicHolidays: [], holidays: [], items: [], totalItems: 0, total: 0};

        const rows = data.publicHolidays ?? data.holidays ?? data.items ?? [];

        this.totalItems = Number(data.totalItems ?? data.total ?? rows.length) || 0;

        const start = (param.currentPage - 1) * param.limit;
        this.holidayItems = rows.map((h, index) => ({
          orderNo: start + index + 1,
          id: h?.id,
          date: h?.date,
          name: h?.name,
        }));
      } catch (err) {
        console.log("Error getHolidayList:", err);
        this.totalItems = 0;
        this.holidayItems = [];
      } finally {
        this.isBusy = false;
      }
    },

    sortCompare(e) {
      this.sortState = {
        key: e.sortBy,
        type: e.sortDesc ? "desc" : "asc",
      };
      this.getHolidayList(this.sortState);
    },
    changePage() {
      this.getHolidayList(this.sortState);
    },

    async deleteHolidayById(id) {
      if (!id) return;
      if (!confirm("ยืนยันการลบรายการนี้?")) return;
      try {
        this.isLoading = true;
        await PublicHolidaysService.deletePublicHolidays(id);
        await this.getHolidayList(this.sortState);
      } catch (err) {
        console.log("Error deletePublicHolidays:", err);
        alert("ลบไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    setErrorText(errors) {
      const errDate = errors.find((e) => e.fieldName === "date");
      if (errDate) {
        alert(errDate.message);
      }
    },
  },
};
</script>

<style scoped>
.centered {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.result-card {
  border: 1px solid #eee;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  opacity: 1;
}

.modal-content {
  max-width: 640px;
  width: 100%;
}

.error-col-field {
  width: 80px;
  text-align: center;
  margin-right: 16px;
}

.error-col-msg {
  flex: 1;
  text-align: left;
}
</style>
